/**
 * The ONLY file in this feature that touches crypto.subtle, createImageBitmap
 * or a canvas. Everything is reached through PipelineEnv so the queue and the
 * page can be tested in jsdom, which has none of these APIs.
 *
 * Order is load-bearing (spec 5.2):
 *   1. magic-byte sniff  2. hash  3. decode  4. megapixel cap
 *   5. downscale (GIF skips 3-7)  6. encode  7. choose body  8. release
 *
 * Rule 3 is a security/quality rule, not a style one: decoding MUST go through
 * createImageBitmap(file, { imageOrientation: 'from-image' }). An <img>-based
 * fallback drops EXIF orientation and would silently rotate every iPhone
 * portrait, so when there is no createImageBitmap we upload the ORIGINAL bytes
 * instead of substituting a lossy decoder.
 */
import {
  JPEG_QUALITY,
  MAX_EDGE,
  MAX_MEGAPIXELS,
  chooseUploadBody,
  sniffImageType,
  type AcceptedMime,
} from './helpers'

export interface ProcessedFile {
  /** The exact body to PUT. ALWAYS carries a correct `.type` - supabase-js ignores contentType for Blobs. */
  body: Blob
  mime: AcceptedMime
  /** lowercase hex SHA-256 of the ORIGINAL bytes; null when crypto.subtle is unavailable. */
  hash: string | null
  /** true when the original bytes were sent unchanged (GIF pass-through or decode fallback). */
  asIs: boolean
  /** true when EXIF/GPS were NOT removed (implied by asIs). */
  metadataRetained: boolean
  /**
   * true ONLY for the size comparison at step 7: the file decoded, encoded, and
   * the ORIGINAL bytes still won. The other two as-is causes - a GIF passed
   * through untouched, and a host with no decoder - never compared anything, so
   * chipping them "kept original (smaller)" claims a measurement that never
   * happened.
   */
  keptOriginal: boolean
  /** Object URL of the DOWNSCALED blob for the row thumbnail; caller must revoke. null for as-is rows. */
  previewUrl: string | null
}

export type ProcessFile = (file: File) => Promise<ProcessedFile>

export interface DecodedBitmap {
  width: number
  height: number
  close(): void
  /** The real ImageBitmap; opaque to everything but PipelineEnv.encode. */
  source: unknown
}

export interface PipelineEnv {
  readHead(file: Blob, n: number): Promise<Uint8Array>
  hash(file: Blob): Promise<string | null>
  hasDecoder(): boolean
  decode(file: Blob): Promise<DecodedBitmap>
  encode(
    bitmap: DecodedBitmap,
    width: number,
    height: number,
    mime: 'image/jpeg' | 'image/png',
    quality: number,
  ): Promise<Blob | null>
  createObjectUrl(blob: Blob): string | null
}

/** Below this the encode is presumed to have produced a blank/broken image. */
const MIN_ENCODED_BYTES = 1024

/** WEBP's magic bytes sit at offset 8-11 (RIFF....WEBP); 12 is the minimum useful head length. */
const SNIFF_HEAD_BYTES = 12

export function hasSubtleCrypto(): boolean {
  return typeof globalThis.crypto?.subtle?.digest === 'function'
}

export function createProcessFile(env: PipelineEnv): ProcessFile {
  return async function processFileWithEnv(file: File): Promise<ProcessedFile> {
    // 1. magic bytes decide what this actually is.
    const head = await env.readHead(file, SNIFF_HEAD_BYTES)
    const sniffed = sniffImageType(head)
    if (!sniffed) throw new Error('not a readable image')

    // 2. hash the ORIGINAL bytes; dedupe is a convenience, never a gate.
    let hash: string | null = null
    try {
      hash = await env.hash(file)
    } catch {
      hash = null
    }

    // The SNIFF decides the body's .type, not the OS-reported file.type:
    // supabase-js takes the FormData part type from the Blob itself, and rule 1
    // already ruled that the filename (and therefore File.type) doesn't get a
    // vote. slice(0, size, mime) is zero-copy - same bytes, corrected type.
    const asIs = (mime: AcceptedMime, keptOriginal = false): ProcessedFile => ({
      body: file.slice(0, file.size, mime),
      mime,
      hash,
      asIs: true,
      metadataRetained: true,
      keptOriginal,
      previewUrl: null,
    })

    // 5. GIF skips decode/encode entirely so animation survives.
    if (sniffed === 'image/gif') return asIs('image/gif')

    // 3. only createImageBitmap may produce a re-encoded body.
    if (!env.hasDecoder()) return asIs(sniffed)

    let decoded: DecodedBitmap
    try {
      decoded = await env.decode(file)
    } catch {
      return asIs(sniffed)
    }

    try {
      // 4. a canvas that silently returns blank above the area limit is worse than a refusal.
      if (decoded.width * decoded.height > MAX_MEGAPIXELS * 1e6) {
        throw new Error('image too large to process')
      }

      const longest = Math.max(decoded.width, decoded.height)
      const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1
      const didResize = scale < 1
      const width = Math.max(1, Math.round(decoded.width * scale))
      const height = Math.max(1, Math.round(decoded.height * scale))

      const outMime: 'image/jpeg' | 'image/png' = sniffed === 'image/png' ? 'image/png' : 'image/jpeg'

      // 6. encode, with the as-is fallback on null / throw / suspiciously tiny.
      let encoded: Blob | null = null
      try {
        encoded = await env.encode(decoded, width, height, outMime, JPEG_QUALITY)
      } catch {
        encoded = null
      }
      if (!encoded || encoded.size < MIN_ENCODED_BYTES) return asIs(sniffed)

      // 7. prefer the encode only when it helps; a resize always wins.
      // The ONE branch that earns the "kept original (smaller)" chip.
      if (chooseUploadBody(file.size, encoded.size, didResize) === 'original') return asIs(sniffed, true)

      return {
        body: encoded,
        mime: outMime,
        hash,
        asIs: false,
        metadataRetained: false,
        keptOriginal: false,
        previewUrl: env.createObjectUrl(encoded),
      }
    } finally {
      // 8. release on EVERY path, including the megapixel refusal.
      decoded.close()
    }
  }
}

// ---- the real browser environment ------------------------------------------
export const browserPipelineEnv: PipelineEnv = {
  async readHead(file, n) {
    return new Uint8Array(await file.slice(0, n).arrayBuffer())
  },

  async hash(file) {
    if (!hasSubtleCrypto()) return null
    const digest = await globalThis.crypto.subtle.digest('SHA-256', await file.arrayBuffer())
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  },

  hasDecoder() {
    return typeof globalThis.createImageBitmap === 'function'
  },

  async decode(file) {
    // imageOrientation 'from-image' is mandatory - see the file header.
    const bitmap = await globalThis.createImageBitmap(file, { imageOrientation: 'from-image' })
    return {
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close(),
      source: bitmap,
    }
  },

  async encode(bitmap, width, height, mime, quality) {
    const source = bitmap.source as CanvasImageSource
    if (typeof OffscreenCanvas === 'function') {
      const canvas = new OffscreenCanvas(width, height)
      try {
        const ctx = canvas.getContext('2d')
        if (!ctx) return null
        ctx.drawImage(source, 0, 0, width, height)
        return await canvas.convertToBlob({ type: mime, quality })
      } finally {
        canvas.width = 0
        canvas.height = 0
      }
    }
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    try {
      const ctx = canvas.getContext('2d')
      if (!ctx) return null
      ctx.drawImage(source, 0, 0, width, height)
      return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, quality))
    } finally {
      canvas.width = 0
      canvas.height = 0
    }
  },

  createObjectUrl(blob) {
    try {
      return URL.createObjectURL(blob)
    } catch {
      return null
    }
  },
}

/** The default implementation the page injects into the queue. */
export const processFile: ProcessFile = createProcessFile(browserPipelineEnv)
